import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Avatar, AvatarStack, type AvatarUser } from "@/components/ui/Avatar";
import { IconButton } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { StatusPill } from "@/components/ui/StatusPill";
import { MobileRowCard } from "@/components/board/mobile/MobileRowCard";
import { RowDetailSheet } from "@/components/board/mobile/RowDetailSheet";
import { BRAND } from "@/lib/tokens";
import { avatarUserFromFlowUser, deriveAvatarColor, deriveInitials } from "@/lib/colors";
import { useBoardPeople } from "@/hooks/useBoardPeople";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import { useAuth } from "@/hooks/useAuth";
import { boardPeopleById } from "@/lib/boardPeople";
import { useBoardPresence } from "@/hooks/useBoardPresence";
import { usePersonalWorkspaceId } from "@/hooks/useWorkspaceMeta";
import { useUnreadSubmissionsCount } from "@/hooks/useUnreadSubmissionsCount";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { ViewBoard, ViewRow } from "@/lib/boardView";
import type { CellValue, UUID } from "@/lib/types";

interface MobileBoardViewProps {
  /** The display board: filtered rows, hidden columns dropped. Drives the cards. */
  board: ViewBoard;
  /** The UNFILTERED board — handed to the detail sheet's Unteraufgaben section, which must
   *  see every column and every child row. See RowDetailSheet.fullBoard. */
  fullBoard?: ViewBoard;
  onPatchRow: (id: string, patch: Partial<ViewRow>) => void;
  onPatchCell?: (rowId: string, columnId: string, value: CellValue) => void;
  /** Unteraufgaben: mint a child row with its title + assignee. */
  onCreateSubtask?: (parentRowId: UUID, title: string, assigneeId: string | null) => void;
  /** Delete a row — provided only for non-guests (write access); omit to hide the control. */
  onDeleteRow?: (rowId: UUID) => void;
  groupByStatus: boolean;
  onAddRow?: () => void;
  /** Jump-to-comment (①): threaded to the detail sheet's comment thread. */
  focusCommentId?: string;
  focusCommentQuery?: string;
}

interface MobileGroup {
  option: { id: string; label: string; color: string } | null;
  rows: ViewRow[];
}

export function MobileBoardView({
  board,
  fullBoard,
  onPatchRow,
  onPatchCell,
  onCreateSubtask,
  onDeleteRow,
  groupByStatus,
  onAddRow,
  focusCommentId,
  focusCommentQuery,
}: MobileBoardViewProps) {
  const navigate = useNavigate();
  const unread = useUnreadSubmissionsCount();
  const [sheetRow, setSheetRow] = useState<ViewRow | null>(null);
  const [tab, setTab] = useState<string>("board");
  // Guests can't create rows (backend 403s); drop the central add FAB entirely
  // rather than leave a dead button that only toasts "coming soon".
  const isGuest = board.access === "guest";
  const navItems = (
    [
      { id: "inbox", icon: "inbox", label: t.mobileInbox, primary: false },
      { id: "board", icon: "layers", label: t.mobileBoards, primary: false },
      { id: "add", icon: "plus", label: "", primary: true },
      { id: "me", icon: "person", label: t.mobileMe, primary: false },
      { id: "more", icon: "dots", label: t.mobileMore, primary: false },
    ] as const
  ).filter((item) => !(isGuest && item.id === "add"));

  const groups: MobileGroup[] = useMemo(() => {
    if (!groupByStatus) return [{ option: null, rows: board.rows }];
    const statusCol = board.columns.find((c) => c.type === "status");
    if (!statusCol?.options) return [{ option: null, rows: board.rows }];
    return statusCol.options
      .map((opt) => ({ option: opt, rows: board.rows.filter((r) => r.status === opt.id) }))
      .filter((g) => g.rows.length > 0);
  }, [board, groupByStatus]);

  const presentIds = useBoardPresence(board.id as UUID);
  const { user } = useAuth();
  const people = useBoardPeople(board.id as UUID);
  const members = useWorkspaceMembers(board.workspace_id as UUID);
  // 🔴 The faces the cards draw. `MobileRowCard` used to resolve owners through
  // `userDirectory.byId`, which searches the DEMO FIXTURE (ids "u1".."u6") — real owner ids are
  // UUIDs, so NOTHING ever matched and `.filter(Boolean)` silently dropped every one. Result:
  // no row card on any phone has ever shown who a task belongs to.
  const peopleById = useMemo(
    () => boardPeopleById(members.data, people.data),
    [members.data, people.data],
  );
  const me = avatarUserFromFlowUser(user);
  const liveUsers = useMemo<AvatarUser[]>(() => {
    const byId = new Map<string, AvatarUser>();
    for (const m of people.data ?? []) {
      const name = m.display_name ?? m.email;
      byId.set(m.user_id, {
        id: m.user_id,
        display_name: name,
        initials: deriveInitials(name),
        avatar_color: deriveAvatarColor(m.user_id),
        username: m.username,
        department_workspace_id: m.department_workspace_id ?? null,
        department_color: m.department_color ?? null,
      });
    }
    return presentIds.map((id) => byId.get(id)).filter((u): u is AvatarUser => Boolean(u));
  }, [presentIds, people.data]);
  // Personal boards are creator-only and unshareable — never surface a "viewing
  // now" indicator there (it can only ever be the owner, and reads as snooping).
  const isPersonalBoard = board.workspace_id === usePersonalWorkspaceId();
  const showPresence = !isPersonalBoard && liveUsers.length > 0;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background: "var(--surface-soft)",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        color: "var(--ink-1)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <header
        className="safe-top"
        style={{
          background: BRAND.blue,
          color: "#fff",
          padding: "12px 14px 10px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <IconButton
          icon="hamburger"
          size={32}
          color="#fff"
          title={t.comingSoon}
          onClick={() => showToast(t.comingSoon)}
        />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
          <span style={{ fontSize: 10.5, color: "rgba(255,255,255,0.7)", letterSpacing: "0.04em" }}>
            Marketing
          </span>
          <span
            style={{
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: "-0.005em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {board.name}
          </span>
        </div>
        <IconButton
          icon="search"
          size={32}
          color="#fff"
          title={t.comingSoon}
          onClick={() => showToast(t.comingSoon)}
        />
        {/* 🔴 Was `userDirectory.me` — the DEMO FIXTURE's `sampleMe`, a hardcoded person.
            Every user on mobile saw the same stranger's initials as their own avatar. It
            survived because the fixture happens to be the repo owner, so it was correct for
            exactly one person. Resolve from the real token, like Topbar does. */}
        {me ? <Avatar user={me} size={28} presence ringColor={BRAND.blue} /> : null}
      </header>

      <div
        style={{
          background: "var(--surface)",
          padding: "8px 14px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          borderBottom: "1px solid var(--hairline-soft)",
          flexShrink: 0,
        }}
      >
        {showPresence ? (
          <>
            <AvatarStack users={liveUsers} size={20} max={3} />
            <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
              <span
                style={{
                  display: "inline-block",
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "#5E8B5F",
                  marginRight: 5,
                  boxShadow: "0 0 0 2px rgba(94,139,95,0.18)",
                }}
              />
              {liveUsers.length} {liveUsers.length === 1 ? t.viewingNowOne : t.viewingNow}
            </span>
          </>
        ) : null}
        <div style={{ flex: 1 }}></div>
        <IconButton
          icon="sliders"
          size={28}
          title={t.comingSoon}
          onClick={() => showToast(t.comingSoon)}
        />
      </div>

      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: "12px 12px calc(100px + env(safe-area-inset-bottom))",
          minHeight: 0,
        }}
      >
        {groups.map((g) => (
          <div key={g.option?.id ?? "all"}>
            {g.option ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 4px 8px" }}
              >
                <StatusPill option={g.option} size="sm" />
                <span
                  style={{
                    fontSize: 11.5,
                    color: "var(--ink-3)",
                    fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
                  }}
                >
                  {g.rows.length}
                </span>
              </div>
            ) : null}
            {g.rows.map((r) => (
              <MobileRowCard
                key={r.id}
                row={r}
                board={board}
                people={peopleById}
                onTap={() => setSheetRow(r)}
                onCheck={(v) => onPatchRow(r.id, { done: v })}
              />
            ))}
          </div>
        ))}
      </div>

      <nav
        className="safe-bottom"
        style={{
          flexShrink: 0,
          background: "var(--surface)",
          borderTop: "1px solid var(--hairline)",
          padding: "8px 0 22px",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "space-around",
          position: "relative",
        }}
      >
        {navItems.map((item) => {
          if (item.primary === true) {
            return (
              <button
                key={item.id}
                aria-label="Neue Zeile"
                onClick={() => (onAddRow ? onAddRow() : showToast(t.comingSoon))}
                title={onAddRow ? "Neue Zeile" : t.comingSoon}
                style={{
                  background: BRAND.orange,
                  color: "#fff",
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 14px rgba(246,129,33,0.35)",
                  cursor: "pointer",
                  marginTop: -12,
                }}
              >
                <Icon name="plus" size={22} color="#fff" stroke={2.2} />
              </button>
            );
          }
          const active = tab === item.id;
          const showBadge = item.id === "inbox" && unread > 0;
          return (
            <button
              key={item.id}
              onClick={() => {
                if (item.id === "inbox") {
                  void navigate({ to: "/postfach" });
                  return;
                }
                setTab(item.id);
              }}
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                color: active ? "var(--accent)" : "var(--ink-3)",
                fontSize: 10.5,
                fontWeight: active ? 600 : 500,
                cursor: "pointer",
                padding: "4px 0",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                position: "relative",
              }}
            >
              <Icon name={item.icon} size={20} stroke={active ? 2 : 1.7} />
              {showBadge ? (
                <span
                  style={{
                    position: "absolute",
                    top: 0,
                    right: "calc(50% - 18px)",
                    minWidth: 16,
                    height: 16,
                    padding: "0 4px",
                    borderRadius: 999,
                    background: BRAND.orange,
                    color: "#fff",
                    fontSize: 9.5,
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1,
                  }}
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              ) : null}
              {item.label}
            </button>
          );
        })}
      </nav>

      {sheetRow ? (
        <RowDetailSheet
          row={sheetRow}
          board={board}
          fullBoard={fullBoard}
          onClose={() => setSheetRow(null)}
          onPatch={(patch) => {
            onPatchRow(sheetRow.id, patch);
            setSheetRow({ ...sheetRow, ...patch });
          }}
          onPatchCell={onPatchCell}
          onCreateSubtask={onCreateSubtask}
          onOpenRow={(rowId) => {
            // "öffnen" on a sub-task swaps the sheet to that row — same surface, same section.
            const next = (fullBoard ?? board).rows.find((r) => r.id === rowId);
            if (next) setSheetRow(next);
          }}
          onDelete={
            onDeleteRow
              ? () => {
                  onDeleteRow(sheetRow.id as UUID);
                  setSheetRow(null);
                }
              : undefined
          }
          focusCommentId={focusCommentId}
          focusCommentQuery={focusCommentQuery}
        />
      ) : null}
    </div>
  );
}
